import AccessTime from '@mui/icons-material/AccessTime';
import { Spacer, Typography } from '@qbe/ads-core';
import React from 'react';
import Countdown from 'react-countdown';

import Modal from '~/components-ds/Modal';
import { useAppContent } from '~/hooks';

interface CountdownRenderer {
  minutes: number;
  seconds: number;
  completed: boolean;
}

interface SessionTimeoutModalProps {
  isOpen: boolean;
  countdown: number;
  onCountdownComplete: () => void;
  onContinue: () => void;
  onExit: () => void;
}

interface CountdownTextProps {
  minutes: number;
  seconds: number;
}

const CountdownText: React.FC<CountdownTextProps> = ({ minutes, seconds }) => {
  const { getTextContent } = useAppContent({
    basePath: 'dsme.idleUserTimer',
    state: {
      minutes,
      seconds,
    },
    tokens: {
      minutes: (state) => state.minutes,
      seconds: (state) => state.seconds,
    },
  });

  return (
    <>
      <Typography variant="h3" component="p">
        {!!minutes && getTextContent('fields.sessionTimeoutModal.countdownMinutes')}{' '}
        {!!minutes && getTextContent('fields.sessionTimeoutModal.countdownSeconds')}
      </Typography>
      <Spacer size="sm" />
    </>
  );
};

export const SessionTimeoutModal: React.FC<SessionTimeoutModalProps> = ({
  isOpen,
  countdown,
  onCountdownComplete,
  onContinue,
  onExit,
}) => {
  const { getTextContent } = useAppContent({
    basePath: 'dsme.idleUserTimer',
  });

  const { getTextContent: getSharedText } = useAppContent({
    basePath: 'shared',
  });

  // Move non-idempotent Date.now() out of render
  const [countdownTarget] = React.useState(() => Date.now() + countdown);

  const countdownRenderer = ({ minutes, seconds, completed }: CountdownRenderer) => {
    if (completed) onCountdownComplete();
    return <CountdownText minutes={minutes} seconds={seconds} />;
  };

  return (
    <Modal
      id="session-timeout-modal"
      open={isOpen}
      analyticsModalName="session timeout modal"
      title={getTextContent('fields.sessionTimeoutModal.title')}
      iconProps={{
        muiIcon: AccessTime,
        color: '#d43900',
      }}
      primaryBtnProps={{
        label: getSharedText('continueModalButtonLabel'),
        onClick: onContinue,
        'data-track': 'Continue - session timeout',
      }}
      secondaryBtnProps={{
        label: getSharedText('exitButtonLabel'),
        onClick: onExit,
        'data-track': 'Exit - session timeout',
      }}
    >
      <>
        <Typography>
          {getTextContent('fields.sessionTimeoutModal.sessionWillExpireText')}
        </Typography>
        <Spacer size="sm" />
        <Countdown date={countdownTarget} renderer={countdownRenderer} />
        <Typography>{getTextContent('fields.sessionTimeoutModal.continueOrExitText')}</Typography>
      </>
    </Modal>
  );
};

export default SessionTimeoutModal;
